import type {ErrorDetails} from './types';

type ErrorPresentation = {title: string; message: string; action?: {label: string; path: string}};

/** Display text is derived from the recorded error. The original remains in Details. */
export function errorPresentation(error: ErrorDetails, title?: string): ErrorPresentation {
  if (error.code === 'desktop_main_sign_out_required' || error.code === 'desktop_account_page_session_ended') {
    return {title: 'Use the main app window', message: error.message};
  }
  if (error.code === 'email_sender_unavailable') return {title: 'Sign-in email is not available',
    message: 'EightBots cannot send a sign-in code. If you have an account password, select Password to sign in.'};
  if (error.code === 'eightbots_credits_required' || error.message.includes('[eightbots_credits_required]')) {
    return {title: 'Not enough available credits',
      message: 'There are not enough available credits to start this request. View your balance, credits in use and next reset.',
      action: {label: 'View credits', path: '/billing'}};
  }
  if (error.name === 'NetworkError') return {title: title ?? 'Connection failed',
    message: 'The connection failed. Check your connection, then check the current status before you try again.'};
  if (error.name === 'RequestTimeout') return {title: title ?? 'No response received',
    message: 'The connection timed out before a response arrived. Check the current status before you try again.'};
  if (error.name === 'InvalidApiResponse') return {title: title ?? 'The response could not be read',
    message: 'The service returned a response that EightBots could not read. Open Details to see the response.'};
  if (error.name === 'ApiUnavailable') return {title: title ?? 'EightBots could not connect',
    message: 'The EightBots service could not be reached. Try connecting again.'};

  // Registrar descriptions are supplied by the registrar and must stay visible verbatim.
  const registrar = /Openprovider|RegistrationError|Domain/.test(error.name);
  const diagnostic = !registrar && (error.name === 'ZodError' ||
    /^(?:Error code: \d{3}\s*-|Traceback \(most recent call last\):|HTTP \d{3}:\s*[<{[])|\n\s+at |\b(?:TypeError|ReferenceError|SyntaxError):|['"]stack['"]\s*:/.test(error.message));
  return {title: title ?? 'The request could not be completed',
    message: diagnostic ? 'Open Details to see the error returned for this request.' : error.message};
}

export function errorDiagnostics(error: ErrorDetails): string {
  const {name, message, stack, ...fields} = error;
  return [`${name}: ${message}`, stack && stack !== `${name}: ${message}` ? stack : '',
    Object.keys(fields).length ? JSON.stringify(fields, null, 2) : ''].filter(Boolean).join('\n\n');
}
